import { replace } from "connected-react-router";
import { useMemo } from "react";
import * as React from "react";
import { useDispatch } from "react-redux";
import { InsDownloadPrivateEquityBenchmarks } from "./insDownloadPrivateEquityBenchmarks";
import { PrivateBenchmarkRequestType, useGetPrivateBenchmarks } from "api/admin/src";
import { getPrivateBenchmarkRows } from "components/config/grid-templates/private-benchmarks-list";
import { BenchmarkTypes } from "components/modal/private-benchmark-list/insBenchmarkSelectionGrid";
import { InsQueryStatedElement } from "components/widgets/base/insQueryStatedElement";
import { CustomColumnType } from "components/widgets/grid/grid-column-types";
import { InsGrid } from "components/widgets/grid/insGrid";
import { isInsightResearchDownloadEnabled, isPreqinShowConstituents } from "hooks/use-tenant-settings";
import { useRefreshOnUserSettingsChange } from "hooks/widget-data/user-settings/user-settings";
import { appInsightEvents } from "language/default";
import { appInsightsTrackEvent } from "plugins/app-insight-init";
import { setSelectedRowsState } from "store/grids";
import { getCurrentLocalTime } from "common/date-utils";
import "../administration.scss";
import "./ins-private-equity-benchmark.scss";
// Standard benchmark sources
export const INSIGHT_RESEARCH_SOURCE = "Insight Research";
export const PREQIN_BENCHMARK_SOURCE = "Preqin Benchmark";
export const PREQIN_INDEX_SOURCE = "Preqin Index";
// List of all standard benchmark sources
export const STANDARD_BENCHMARK_SOURCES = [
    INSIGHT_RESEARCH_SOURCE,
    PREQIN_BENCHMARK_SOURCE,
    PREQIN_INDEX_SOURCE
];
// Grid column definitions for Standard tab
const cols = [
    {
        field: "checkboxCol",
        floatingFilter: false,
        headerCheckboxSelection: true,
        headerName: " ",
        type: CustomColumnType.AUX_CHECKBOX_COLUMN,
        width: 32
    },
    {
        field: "name",
        headerName: "Benchmark Name",
        sort: "asc",
        type: [CustomColumnType.InsLinkColumn]
    },
    { field: "source", headerName: "Source", type: [CustomColumnType.AUX_TEXT_COLUMN] },
    { field: "strategy", headerName: "Strategy", type: [CustomColumnType.AUX_TEXT_COLUMN] },
    { field: "geography", headerName: "Geography", type: [CustomColumnType.AUX_TEXT_COLUMN] },
    { cellDataType: false, field: "vintage", headerName: "Vintage Year", type: [CustomColumnType.AUX_TEXT_COLUMN] }
];
/**
 * Maps the raw source value from the API to the standardized BenchmarkSource type
 * @param rawSource - The source value from the API
 * @returns The standardized BenchmarkSource value
 */
export const mapSourceToBenchmarkSource = (rawSource) => {
    if (!rawSource) {
        return PREQIN_BENCHMARK_SOURCE;
    }
    const normalizedSource = rawSource.toLowerCase().trim();
    if (normalizedSource === "insight research") {
        return INSIGHT_RESEARCH_SOURCE;
    }
    if (normalizedSource === "preqin index" || normalizedSource.includes("policy")) {
        return PREQIN_INDEX_SOURCE;
    }
    // Default to Preqin Benchmark for "Preqin" or any other Preqin-related source
    return PREQIN_BENCHMARK_SOURCE;
};
/**
 * Determines if download is allowed for a benchmark based on its source and user permissions
 * @param source - The benchmark source type
 * @param hasInsightResearchPermission - Whether the user has Insight Research download permission
 * @param hasPreqinPermission - Whether the user has Preqin download permission
 * @returns Whether download is allowed
 */
export const isDownloadAllowed = (source, hasInsightResearchPermission, hasPreqinPermission) => {
    switch (source) {
        case INSIGHT_RESEARCH_SOURCE:
            // Insight Research always shows download button based on permission
            return hasInsightResearchPermission;
        case PREQIN_BENCHMARK_SOURCE:
        case PREQIN_INDEX_SOURCE:
            // Preqin Benchmarks and Preqin Policy Indices follow same permission-based behavior
            return hasPreqinPermission;
        default:
            return false;
    }
};
/**
 * Filters benchmarks to only include standard (non-custom) benchmarks
 * @param benchmarks - Array of benchmark data
 * @returns Filtered array of standard benchmarks
 */
const filterStandardBenchmarks = (benchmarks) => benchmarks.filter((benchmark) => !benchmark.summary?.isCustom && benchmark.summary?.type === BenchmarkTypes.Standard);
export const InsPrivateEquityStandardContent = ({ stateId }) => {
    const dispatch = useDispatch();
    const allowInsightResearchExport = isInsightResearchDownloadEnabled();
    const allowPreqinExport = isPreqinShowConstituents();
    const [fetchedRows, querystate] = useGetPrivateBenchmarks({ type: PrivateBenchmarkRequestType.Standard }, {
        update: { PrivateBenchmarks: (_, transformedValue) => ({ ...transformedValue }) }
    });
    useRefreshOnUserSettingsChange();
    const rowData = useMemo(() => {
        // Filter to only standard benchmarks (non-custom)
        const standardBenchmarks = filterStandardBenchmarks(fetchedRows);
        const rowsWithLinks = standardBenchmarks.map((r) => {
            const mappedSource = mapSourceToBenchmarkSource(r.summary?.source);
            return {
                ...r.summary,
                // Map the source to standardized value
                name: {
                    label: r.summary?.name,
                    onClick: () => {
                        appInsightsTrackEvent({ name: appInsightEvents.customEvents.names.OpenBenchmarkDetails,
                            properties: { LocalTimestamp: getCurrentLocalTime() } }, {
                            benchmarkName: r.summary?.name
                        });
                        dispatch(replace(`/administration/market-data/private-equity-benchmarks/${r.summary?.id}?isCustom=false`));
                    }
                },
                source: mappedSource
            };
        });
        return getPrivateBenchmarkRows(rowsWithLinks, () => true);
    }, [fetchedRows, dispatch]);
    // Determine if any export is allowed based on selected rows
    const allowExport = allowInsightResearchExport || allowPreqinExport;
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "tabHeaderRight" }, allowExport &&
            React.createElement(InsDownloadPrivateEquityBenchmarks, { templateOnly: false, stateId: stateId, isCustom: false, appInsightsOperationName: appInsightEvents.customEvents.names.BenchmarkExportSelection })),
        React.createElement("div", { className: "defaultIndicesGrid" },
            React.createElement(InsQueryStatedElement, { queryState: querystate },
                React.createElement(InsGrid, { stateId: stateId, rowData: rowData, queryState: querystate, applyColumnsAutosize: true, selectionMode: allowExport ? "multiple" : "single", rowSelectedHandler: (e) => {
                        dispatch(setSelectedRowsState({
                            id: stateId,
                            selectedRows: e.api.getSelectedRows().map((row) => row?.id?.toString())
                        }));
                    }, columnDefs: cols })))));
};
